import { Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

interface AdminSearchInputProps {
  /** The committed query. Kept in sync with what the caller filters on. */
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Names what is being narrowed: "Filter tenants by name". */
  label: string;
  /**
   * Held back this long before the caller hears about a keystroke. Zero for a
   * search that only filters rows already in memory, where the round trip is
   * free and a delay is just lag.
   */
  debounceMs?: number;
  className?: string;
}

/**
 * The one search box in the admin panel.
 *
 * It keeps its own draft so the field stays responsive while the caller is
 * debounced, and syncs back when the committed value changes underneath it —
 * which is how a "Clear all" button empties a field it does not own.
 */
export function AdminSearchInput({
  value,
  onChange,
  placeholder,
  label,
  debounceMs = 0,
  className,
}: AdminSearchInputProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return;
    if (debounceMs === 0) {
      onChangeRef.current(draft);
      return;
    }
    const timer = window.setTimeout(() => onChangeRef.current(draft), debounceMs);
    return () => window.clearTimeout(timer);
    // `value` is read to skip the echo of a commit this effect just made; it is
    // not what the timer is waiting on, so it stays out of the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, debounceMs]);

  return (
    <label
      className={cn(
        "relative flex h-8 min-w-0 items-center gap-2 rounded-sm border border-input-border bg-input-background px-2.5 text-xs text-muted-foreground transition-colors focus-within:border-input-focus-border",
        className,
      )}
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        // The browser's own clear affordance is a WebKit-only glyph that lands
        // beside ours, so the field renders one of them and it is ours.
        className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground/70 [&::-webkit-search-cancel-button]:appearance-none"
      />
      {draft ? (
        <button
          type="button"
          onClick={() => setDraft("")}
          aria-label={t("admin.layout.search.clear", "Clear search")}
          className="shrink-0 rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
    </label>
  );
}
